import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import BottomNav from "../components/BottomNav";
import SEO from "../components/SEO";
import MediaEditor from "../components/editor/MediaEditor";
import { useAuth } from "../lib/auth";
import api, { BACKEND_URL, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sparkles, Film, Image as ImageIcon, Upload, Wand2, Palette, FolderOpen,
  Clock, Download, Trash2, Pencil, Plus, Scissors, Zap, Layers, Play,
  Loader2, Copy, Check, Rocket, Star, Music2, MessageSquare, ChevronRight,
} from "lucide-react";
import AutoClipsPanel from "../components/editor/AutoClipsPanel";
import BrandKitEditor from "../components/editor/BrandKitEditor";
import CreatorStudioOnboarding, { hasSeenCreatorStudioTour, resetCreatorStudioTour, CreatorStudioHelpButton } from "../components/editor/CreatorStudioOnboarding";

const TEMPLATES = [
  { id: "product-reveal", title: "Product reveal", vibe: "Punchy launch", filter: "vivid", aspect: "9:16", textOverlay: "IT'S HERE 🔥", textPos: "middle", textAnim: "pop", textColor: "#FFFFFF", icon: Rocket, tone: "text-nexus-pink" },
  { id: "before-after", title: "Before → After", vibe: "Transformation reveal", filter: "cinema", aspect: "9:16", textOverlay: "BEFORE / AFTER", textPos: "top", textAnim: "fade", textColor: "#FFFFFF", icon: Wand2, tone: "text-nexus-purple" },
  { id: "unboxing", title: "Unboxing", vibe: "Excited reveal", filter: "warm", aspect: "9:16", textOverlay: "YOU NEED TO SEE THIS 👀", textPos: "top", textAnim: "pop", textColor: "#FFEA00", icon: Sparkles, tone: "text-amber-300" },
  { id: "tutorial", title: "Tutorial", vibe: "Clean & clear", filter: "portrait", aspect: "9:16", textOverlay: "STEP 1", textPos: "top", textAnim: "slide", textColor: "#FFFFFF", icon: Play, tone: "text-nexus-blue" },
  { id: "pov", title: "POV moment", vibe: "Cinematic pov", filter: "mono", aspect: "9:16", textOverlay: "POV:", textPos: "top", textAnim: "static", textColor: "#FFFFFF", icon: Star, tone: "text-emerald-300" },
  { id: "testimonial", title: "Testimonial", vibe: "Honest review vibe", filter: "fade", aspect: "9:16", textOverlay: "REVIEW", textPos: "bottom", textAnim: "fade", textColor: "#FFFFFF", icon: MessageSquare, tone: "text-nexus-pink" },
];

const TABS = [
  { id: "recent", label: "Recent", icon: FolderOpen },
  { id: "new", label: "New project", icon: Plus },
  { id: "templates", label: "Templates", icon: Layers },
  { id: "brand-kits", label: "Brand kits", icon: Palette },
  { id: "auto-clips", label: "Auto clips", icon: Scissors },
  { id: "exports", label: "Exports", icon: Download },
];

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

function fmtRel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.round((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function StatPill({ icon: Icon, label, value, tone = "text-white" }) {
  return (
    <div className="glass rounded-2xl p-4 min-w-[140px]" data-testid={`studio-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
      </div>
      <div className="font-display font-black text-2xl tabular-nums text-white">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

export default function CreatorStudio() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "recent";
  const [tab, setTab] = useState(initialTab);
  const [drafts, setDrafts] = useState([]);
  const [exports, setExports] = useState([]);
  const [kits, setKits] = useState([]);
  const [quota, setQuota] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editorState, setEditorState] = useState(null); // { file, draftId, template }
  const [resuming, setResuming] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Open the tour once per creator, after the shell has painted
    if (loading || !user || user.role !== "creator") return;
    if (hasSeenCreatorStudioTour()) return;
    const timer = setTimeout(() => setShowTour(true), 800);
    return () => clearTimeout(timer);
  }, [loading, user]);

  const replayTour = () => {
    resetCreatorStudioTour();
    setShowTour(true);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth?mode=login"); return; }
    if (user.role !== "creator") {
      toast.error("Studio is for creators");
      nav(user.role === "brand" ? "/brand" : "/");
      return;
    }
    refreshAll();
  }, [user, loading]);

  useEffect(() => {
    // Keep URL query synced so back/forward works and links are shareable
    const current = params.get("tab");
    if (current !== tab) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
  }, [tab]);

  const refreshAll = async () => {
    setBusy(true);
    try {
      const [dR, eR, kR, qR] = await Promise.all([
        api.get("/studio/drafts").catch(() => ({ data: { drafts: [] } })),
        api.get("/studio/exports").catch(() => ({ data: { exports: [] } })),
        api.get("/studio/brand-kits").catch(() => ({ data: { kits: [] } })),
        api.get("/studio/quota").catch(() => ({ data: null })),
      ]);
      setDrafts(dR.data?.drafts || []);
      setExports(eR.data?.exports || []);
      setKits(kR.data?.kits || []);
      setQuota(qR.data);
    } finally { setBusy(false); }
  };

  const openEditor = (file, extras = {}) => {
    setEditorState({ file, ...extras });
  };

  const closeEditor = () => {
    setEditorState(null);
    refreshAll();
  };

  const onUploadNew = (file) => {
    if (!file) return;
    openEditor(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resumeDraft = async (d) => {
    if (!d.source_url || d.source_url.startsWith("local://")) {
      toast.error("This draft has no persisted source file — re-upload to continue");
      return;
    }
    setResuming(d.id);
    try {
      const resp = await fetch(toAbsolute(d.source_url));
      if (!resp.ok) throw new Error(`Fetch failed ${resp.status}`);
      const blob = await resp.blob();
      const type = blob.type || (d.kind === "video" ? "video/mp4" : "image/jpeg");
      const ext = type.includes("mp4") ? "mp4" : type.includes("webm") ? "webm" : type.includes("png") ? "png" : (d.kind === "video" ? "mp4" : "jpg");
      const file = new File([blob], d.title || `draft.${ext}`, { type });
      openEditor(file, { draftId: d.id });
    } catch (e) {
      toast.error("Couldn't load draft source");
    } finally { setResuming(null); }
  };

  const deleteDraft = async (d) => {
    if (!window.confirm("Delete this draft? This can't be undone.")) return;
    try {
      await api.delete(`/studio/drafts/${d.id}`);
      setDrafts((rows) => rows.filter((r) => r.id !== d.id));
      toast.success("Draft removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const applyTemplateAndOpen = (tmpl) => {
    // No file yet — open the file picker; on select, open editor with template preset.
    if (!fileInputRef.current) return;
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "image/*,video/mp4,video/webm,video/quicktime";
    inp.onchange = (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      openEditor(file, { template: tmpl });
    };
    inp.click();
  };

  const stats = useMemo(() => ({
    drafts: drafts.length,
    exports: exports.length,
    videos: drafts.filter((d) => d.kind === "video").length,
    images: drafts.filter((d) => d.kind === "image").length,
    kits: kits.length,
  }), [drafts, exports, kits]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen grain" data-testid="creator-studio-page">
      <Navbar />
      <SEO title="Creator Studio · Nexus" description="Edit, remix, and ship your content — all in one place." path="/creator-studio" />

      {editorState && (
        <MediaEditor
          file={editorState.file}
          draftId={editorState.draftId || null}
          initialTemplate={editorState.template || null}
          onCancel={closeEditor}
          onExport={closeEditor}
          onDraftSaved={() => refreshAll()}
        />
      )}

      {showTour && (
        <CreatorStudioOnboarding
          onClose={() => setShowTour(false)}
          onFocusTab={(t) => setTab(t)}
        />
      )}

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10 space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Wand2 className="h-3.5 w-3.5 text-nexus-pink" /> Creator Studio
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
              Your <span className="nexus-gradient-text">edit room</span>.
            </h1>
            <p className="text-zinc-400 mt-2 text-sm max-w-xl">
              Drafts, templates, brand kits, auto-generated clips and every export you&apos;ve ever made — one place.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <CreatorStudioHelpButton onClick={replayTour} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={(e) => onUploadNew(e.target.files?.[0])}
              data-testid="studio-new-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold"
              data-testid="studio-new-project-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> New project
            </Button>
            <Link to="/creator/profile">
              <Button
                variant="outline"
                className="rounded-full border-white/10 text-white hover:bg-white/5 h-10"
                data-testid="studio-back-to-profile"
              >
                Back to profile
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats bento */}
        <div className="flex gap-3 flex-wrap">
          <StatPill icon={FolderOpen} label="Drafts" value={stats.drafts} tone="text-nexus-pink" />
          <StatPill icon={Film} label="Videos" value={stats.videos} tone="text-nexus-purple" />
          <StatPill icon={ImageIcon} label="Images" value={stats.images} tone="text-nexus-blue" />
          <StatPill icon={Download} label="Exports" value={stats.exports} tone="text-emerald-300" />
          <StatPill icon={Palette} label="Brand kits" value={stats.kits} tone="text-amber-300" />
          {quota && (
            <StatPill
              icon={Zap}
              label={quota.is_subscribed ? "Pro" : "Free exports"}
              value={quota.is_subscribed ? "Unlimited" : `${quota.used}/${quota.limit}`}
              tone={quota.is_subscribed ? "text-emerald-300" : "text-nexus-pink"}
            />
          )}
        </div>

        {/* Tab strip */}
        <div className="glass rounded-2xl p-1.5 flex gap-1 overflow-x-auto no-scrollbar" data-testid="studio-tabs">
          {TABS.map((t) => {
            const Ic = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold uppercase tracking-widest transition-all ${
                  active ? "nexus-gradient text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`studio-tab-${t.id}`}
              >
                <Ic className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {tab === "recent" && (
          <RecentTab
            drafts={drafts}
            busy={busy}
            resumingId={resuming}
            onResume={resumeDraft}
            onDelete={deleteDraft}
            onOpenNew={() => fileInputRef.current?.click()}
          />
        )}
        {tab === "new" && (
          <NewProjectTab
            onPickFile={() => fileInputRef.current?.click()}
            onOpenTemplates={() => setTab("templates")}
            onOpenAutoClips={() => setTab("auto-clips")}
          />
        )}
        {tab === "templates" && (
          <TemplatesTab onApply={applyTemplateAndOpen} />
        )}
        {tab === "brand-kits" && (
          <BrandKitsTab kits={kits} onChange={refreshAll} />
        )}
        {tab === "auto-clips" && (
          <AutoClipsPanel onOpenClip={(file, meta) => openEditor(file, { autoClipMeta: meta })} />
        )}
        {tab === "exports" && (
          <ExportsTab exports={exports} onRefresh={refreshAll} />
        )}
      </div>
      <BottomNav />
    </div>
  );
}

// ---------------- Tab: Recent ----------------
function RecentTab({ drafts, busy, resumingId, onResume, onDelete, onOpenNew }) {
  if (busy && drafts.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="aspect-square rounded-xl bg-white/[0.03] animate-pulse" />
        ))}
      </div>
    );
  }
  if (drafts.length === 0) {
    return (
      <div className="glass rounded-3xl p-12 text-center" data-testid="studio-recent-empty">
        <Sparkles className="h-6 w-6 mx-auto mb-3 text-nexus-pink" />
        <div className="font-display text-2xl mb-2">No drafts yet</div>
        <p className="text-zinc-500 max-w-md mx-auto mb-5">
          Every save shows up here, ready to resume from any device. Kick off your first edit in one click.
        </p>
        <Button onClick={onOpenNew} className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold" data-testid="studio-recent-empty-cta">
          <Plus className="h-4 w-4 mr-2" /> Start editing
        </Button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="studio-recent-grid">
      {drafts.map((d) => {
        const abs = toAbsolute(d.thumb_url || d.source_url);
        const video = d.kind === "video";
        const isLocal = (d.source_url || "").startsWith("local://");
        const isResuming = resumingId === d.id;
        return (
          <div
            key={d.id}
            className="relative aspect-square rounded-xl overflow-hidden hairline group bg-black"
            data-testid={`studio-draft-${d.id}`}
          >
            {isLocal ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-2 bg-white/[0.02]">
                <ImageIcon className="h-6 w-6" />
                <span className="text-[10px] uppercase tracking-widest">Source not persisted</span>
              </div>
            ) : video ? (
              <video src={abs} className="w-full h-full object-cover" muted playsInline preload="metadata" />
            ) : (
              <img src={abs} alt="" className="w-full h-full object-cover" />
            )}

            <div className="absolute top-2 left-2 flex gap-1.5">
              <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest flex items-center gap-1">
                {video ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                {video ? "Video" : "Image"}
              </span>
              {d.hire_id && (
                <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest flex items-center gap-1 text-emerald-300">
                  <Sparkles className="h-3 w-3" /> Collab
                </span>
              )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/85 via-black/60 to-transparent">
              <div className="text-[11px] font-semibold truncate text-white">{d.title || "Untitled draft"}</div>
              <div className="text-[10px] text-zinc-400 flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{fmtRel(d.updated_at)}</div>
            </div>

            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
              <Button
                onClick={() => onResume(d)}
                disabled={isResuming || isLocal}
                className="nexus-gradient text-white border-0 rounded-full h-9 px-4 font-semibold disabled:opacity-50"
                data-testid={`studio-draft-resume-${d.id}`}
              >
                {isResuming
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Loading…</>
                  : <><Pencil className="h-3.5 w-3.5 mr-1.5" /> Resume</>}
              </Button>
              <button
                onClick={() => onDelete(d)}
                className="text-[11px] uppercase tracking-widest text-red-300 hover:text-red-100 flex items-center gap-1"
                data-testid={`studio-draft-delete-${d.id}`}
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Tab: New project ----------------
function NewProjectTab({ onPickFile, onOpenTemplates, onOpenAutoClips }) {
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="studio-new-tab">
      <button
        onClick={onPickFile}
        className="text-left rounded-3xl p-8 relative overflow-hidden border border-white/10 bg-gradient-to-br from-nexus-pink/15 via-fuchsia-500/5 to-nexus-blue/15 hover:border-nexus-pink/40 transition-colors group"
        data-testid="studio-new-upload-card"
      >
        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-nexus-pink/20 blur-3xl group-hover:bg-nexus-pink/40 transition-colors" />
        <Upload className="h-8 w-8 text-nexus-pink mb-4" />
        <div className="font-display text-2xl font-bold tracking-tight mb-2">Upload media</div>
        <p className="text-sm text-zinc-400 mb-6">Video or image · up to 20 MB. Land straight in the editor.</p>
        <div className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-nexus-pink font-semibold">
          Choose file <ChevronRight className="h-3 w-3" />
        </div>
      </button>

      <button
        onClick={onOpenTemplates}
        className="text-left rounded-3xl p-8 relative overflow-hidden border border-white/10 bg-gradient-to-br from-nexus-purple/15 via-nexus-blue/5 to-transparent hover:border-nexus-purple/40 transition-colors group"
        data-testid="studio-new-template-card"
      >
        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-nexus-purple/20 blur-3xl group-hover:bg-nexus-purple/40 transition-colors" />
        <Layers className="h-8 w-8 text-nexus-purple mb-4" />
        <div className="font-display text-2xl font-bold tracking-tight mb-2">Start from a template</div>
        <p className="text-sm text-zinc-400 mb-6">6 collab-ready looks — product reveal, before/after, unboxing and more.</p>
        <div className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-nexus-purple font-semibold">
          Browse templates <ChevronRight className="h-3 w-3" />
        </div>
      </button>

      <button
        onClick={onOpenAutoClips}
        className="text-left rounded-3xl p-8 relative overflow-hidden border border-white/10 bg-gradient-to-br from-emerald-500/15 via-nexus-blue/5 to-transparent hover:border-emerald-400/40 transition-colors group"
        data-testid="studio-new-autoclips-card"
      >
        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-emerald-500/20 blur-3xl group-hover:bg-emerald-500/40 transition-colors" />
        <Scissors className="h-8 w-8 text-emerald-300 mb-4" />
        <div className="font-display text-2xl font-bold tracking-tight mb-2">Auto clips</div>
        <p className="text-sm text-zinc-400 mb-6">Drop a long video · Claude picks the best short-form moments for you.</p>
        <div className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-emerald-300 font-semibold">
          Try auto clips <ChevronRight className="h-3 w-3" />
        </div>
      </button>
    </div>
  );
}

// ---------------- Tab: Templates ----------------
function TemplatesTab({ onApply }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="studio-templates-grid">
      {TEMPLATES.map((t) => {
        const Ic = t.icon;
        return (
          <button
            key={t.id}
            onClick={() => onApply(t)}
            className="text-left glass rounded-2xl p-6 hover:border-nexus-pink/40 transition-all group"
            data-testid={`studio-template-${t.id}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <Ic className={`h-5 w-5 ${t.tone}`} />
              </div>
              <div>
                <div className="font-display font-bold text-lg tracking-tight">{t.title}</div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t.vibe}</div>
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap text-[10px] uppercase tracking-widest">
              <span className="glass px-2 py-0.5 rounded-full">{t.aspect}</span>
              <span className="glass px-2 py-0.5 rounded-full">Filter: {t.filter}</span>
              <span className="glass px-2 py-0.5 rounded-full">Anim: {t.textAnim}</span>
            </div>
            <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-nexus-pink font-semibold group-hover:text-white transition-colors">
              Use template <ChevronRight className="h-3 w-3" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------- Tab: Brand kits ----------------
function BrandKitsTab({ kits, onChange }) {
  const [editing, setEditing] = useState(null); // kit object or 'new' for create

  const remove = async (kit) => {
    if (!window.confirm(`Delete brand kit "${kit.name}"?`)) return;
    try {
      await api.delete(`/studio/brand-kits/${kit.id}`);
      toast.success("Brand kit removed");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (editing) {
    return (
      <BrandKitEditor
        kit={editing === "new" ? null : editing}
        onSaved={() => { setEditing(null); onChange(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="studio-brand-kits-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Save your colours, logo and tagline so every edit stays on-brand.
        </div>
        <Button
          onClick={() => setEditing("new")}
          className="nexus-gradient text-white border-0 rounded-full h-9 px-4 font-semibold"
          data-testid="studio-kit-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> New brand kit
        </Button>
      </div>
      {kits.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="studio-kits-empty">
          <Palette className="h-6 w-6 mx-auto mb-3 text-nexus-pink" />
          <div className="font-display text-2xl mb-2">No brand kits yet</div>
          <p className="text-zinc-500 max-w-md mx-auto mb-5">
            One-tap consistency for every edit. Pick your palette, drop your logo, save the tagline.
          </p>
          <Button onClick={() => setEditing("new")} className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold" data-testid="studio-kits-empty-cta">
            <Plus className="h-4 w-4 mr-2" /> Create your first kit
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {kits.map((k) => (
            <div key={k.id} className="glass rounded-2xl p-5" data-testid={`studio-kit-${k.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg tracking-tight truncate">{k.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Updated {fmtRel(k.updated_at)}</div>
                </div>
                {k.logo_url && (
                  <img src={toAbsolute(k.logo_url)} alt="" className="h-10 w-10 rounded-xl object-cover" />
                )}
              </div>
              <div className="flex gap-1.5 mb-4">
                {[k.primary_color, k.secondary_color, k.accent_color].map((c, i) => (
                  <div key={i} className="h-8 flex-1 rounded-lg border border-white/10" style={{ background: c }} />
                ))}
              </div>
              <div className="text-xs text-zinc-400 mb-4 line-clamp-2 min-h-[32px]">{k.tagline || <em className="text-zinc-600">No tagline yet</em>}</div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setEditing(k)}
                  variant="outline"
                  className="rounded-full border-white/10 text-white hover:bg-white/5 h-8 flex-1"
                  data-testid={`studio-kit-edit-${k.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
                <button
                  onClick={() => remove(k)}
                  className="text-red-300 hover:text-red-100 h-8 w-8 rounded-full flex items-center justify-center hover:bg-red-500/10 transition-colors"
                  data-testid={`studio-kit-delete-${k.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Tab: Exports ----------------
function ExportsTab({ exports, onRefresh }) {
  const [copyId, setCopyId] = useState(null);
  const copy = (url, id) => {
    const abs = toAbsolute(url);
    navigator.clipboard.writeText(abs).then(() => {
      setCopyId(id); setTimeout(() => setCopyId(null), 2000);
      toast.success("URL copied");
    });
  };

  if (exports.length === 0) {
    return (
      <div className="glass rounded-3xl p-12 text-center" data-testid="studio-exports-empty">
        <Download className="h-6 w-6 mx-auto mb-3 text-emerald-300" />
        <div className="font-display text-2xl mb-2">No exports yet</div>
        <p className="text-zinc-500 max-w-md mx-auto">Every finished edit shows up here with a shareable link.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="studio-exports-grid">
      {exports.map((e) => (
        <div key={e.id} className="glass rounded-2xl overflow-hidden group" data-testid={`studio-export-${e.id}`}>
          <div className="aspect-square bg-black relative">
            {e.thumb_url ? (
              <img src={toAbsolute(e.thumb_url)} alt="" className="w-full h-full object-cover" />
            ) : e.output_url && e.kind === "video" ? (
              <video src={toAbsolute(e.output_url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
            ) : e.output_url ? (
              <img src={toAbsolute(e.output_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-600">
                {e.kind === "video" ? <Film className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
              </div>
            )}
            <div className="absolute top-2 left-2 flex gap-1.5">
              <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest flex items-center gap-1">
                {e.kind === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                {e.kind}
              </span>
              {e.dimensions && (
                <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest">{e.dimensions}</span>
              )}
            </div>
          </div>
          <div className="p-4">
            <div className="font-semibold text-sm text-white truncate mb-1">{e.title || "Untitled export"}</div>
            <div className="text-[11px] text-zinc-500 flex items-center gap-2 mb-3">
              <Clock className="h-3 w-3" /> {fmtRel(e.created_at)}
              {e.filesize ? <span className="text-zinc-600">· {fmtBytes(e.filesize)}</span> : null}
              {e.duration_s ? <span className="text-zinc-600">· {e.duration_s.toFixed(1)}s</span> : null}
            </div>
            <div className="flex gap-2">
              {e.output_url && (
                <a
                  href={toAbsolute(e.output_url)}
                  download
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full h-8 border border-white/10 text-white hover:bg-white/5 text-[11px] uppercase tracking-widest font-semibold"
                  data-testid={`studio-export-download-${e.id}`}
                >
                  <Download className="h-3 w-3" /> Download
                </a>
              )}
              {e.output_url && (
                <button
                  onClick={() => copy(e.output_url, e.id)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full h-8 px-3 border border-white/10 text-white hover:bg-white/5 text-[11px] uppercase tracking-widest font-semibold"
                  data-testid={`studio-export-copy-${e.id}`}
                >
                  {copyId === e.id ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
